import { safeLocalStorage } from "../../utils/safeLocalStorage";
import { useTenantScope } from "../../contexts/TenantScopeContext";
/**
 * Compatibility wrapper for useStytchOrganization that works in both:
 * - Cookie-based auth (normal app): Uses Stytch SDK directly
 * - Bearer token auth (iframe embed): Fetches from /api/ai/session/me and caches
 *
 * This allows the same code to work in both authentication modes.
 */

import { useEffect, useState } from "react";
import { useStytchOrganization } from "@stytch/react/b2b";
import { useEmbedAuth } from "../../contexts/EmbedAuthContext";
import { clearSessionMeCache, fetchSessionMe, getCachedSessionMe } from "./sessionMeCache";

const CACHE_KEY_ORGANIZATION = "embed_organization_cache";

interface Organization {
    organization_id: string;
    organization_slug: string;
    organization_name?: string; // Only available in cookie-based auth (from Stytch SDK)
    trusted_metadata?: Record<string, any>;
}

interface OrganizationHook {
    organization: Organization | null;
}

export function useCompatOrganization(): OrganizationHook {
    const tenantScope = useTenantScope();
    // Try Stytch SDK first (works in cookie-based auth)
    const stytchOrg = useStytchOrganization();

    // Check embed auth context (works in Bearer token auth)
    const embedAuth = useEmbedAuth();

    // State for cached organization data in embed mode. Seeded synchronously
    // from the shared session/me cache so a hook instance mounting after
    // another instance already resolved the fetch adopts it immediately.
    const [cachedOrganization, setCachedOrganization] = useState<Organization | null>(
        () => getCachedSessionMe()?.organization ?? null,
    );

    // Embed mode: load from cache on mount. Partial caches are dropped so the
    // fetch effect below re-runs once and re-caches the full /session/me shape:
    // native chrome needs organization_name, and feature gates need
    // trusted_metadata.
    useEffect(() => {
        const cached = safeLocalStorage.getItem(CACHE_KEY_ORGANIZATION);
        if (cached) {
            try {
                const parsed: Organization = JSON.parse(cached);
                if (!parsed.organization_name || parsed.trusted_metadata === undefined) {
                    safeLocalStorage.removeItem(CACHE_KEY_ORGANIZATION);
                    return;
                }
                setCachedOrganization(parsed);
            } catch (e) {
                console.error("[useCompatOrganization] Failed to parse cached organization:", e);
                safeLocalStorage.removeItem(CACHE_KEY_ORGANIZATION);
            }
        }
    }, []);

    // Embed mode: fetch from API if authenticated but no cache. Keyed on the
    // sessionJwt (not just isAuthenticated) so a fetch that raced a stale JWT
    // and 401'd is retried after the token refresh swaps in a fresh one —
    // otherwise the org (and everything scoped by it, e.g. the native shell's
    // setContext) never resolves for the rest of the session. Goes through
    // the shared sessionMeCache so concurrent mounts (and useCompatMember's
    // own fetch) collapse onto a single in-flight request.
    const sessionJwt = embedAuth.tokens?.sessionJwt;
    useEffect(() => {
        if (
            embedAuth.isAuthenticated &&
            sessionJwt &&
            !cachedOrganization &&
            !stytchOrg.organization
        ) {
            fetchSessionMe()
                .then((data) => {
                    const organization = data.organization;
                    setCachedOrganization(organization);
                    safeLocalStorage.setItem(CACHE_KEY_ORGANIZATION, JSON.stringify(organization));
                })
                .catch((error) => {
                    console.error("[useCompatOrganization] Failed to fetch organization:", error);
                });
        }
    }, [embedAuth.isAuthenticated, sessionJwt, cachedOrganization, stytchOrg.organization]);

    // Clear cache when tokens expire or are cleared
    useEffect(() => {
        if (!embedAuth.isAuthenticated) {
            setCachedOrganization(null);
            safeLocalStorage.removeItem(CACHE_KEY_ORGANIZATION);
            clearSessionMeCache();
        }
    }, [embedAuth.isAuthenticated]);

    // CONDITIONAL LOGIC AND RETURN AFTER ALL HOOKS

    if (tenantScope.mode === "canonical")
        return { organization: tenantScope.sessionOrganization ?? null };

    // If Stytch SDK has organization data, use it (normal cookie-based flow)
    if (stytchOrg.organization) {
        return { organization: stytchOrg.organization as Organization };
    }

    // Return cached organization in embed mode
    return { organization: cachedOrganization };
}
